"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import type {
  InteractiveEditingData,
  EditingTurn,
  LibraryItem,
} from "../types/library";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Save,
  Share,
  ArrowLeft,
  Trash2,
  Maximize,
  Minimize,
  X,
  Sparkles,
  Zap,
} from "lucide-react";
import { useSelector } from "react-redux";
import { selectUserGlobalData } from "../store/dreamworldSlice";
import dreamWorldProvider from "../DreamWorldProvider";
import { Modality } from "@google/genai";
import uploadImageToFirebase from "../utils/uploadImageToFirebase";
import { getBlob, ref } from "firebase/storage";
import { storage } from "../utils/firebase";
import { Timestamp } from "firebase/firestore";
import PublishContent from "../components/create/PublishContent";
import { v4 as uuidv4 } from "uuid";
import eventBus from "../utils/eventBus";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import { hasEnoughFlux, decreaseFluxBalance } from "../utils/fluxManager";
import InsufficientFluxModal from "../components/modals/InsufficientFluxModal";

const InteractiveEditingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [editingData, setEditingData] = useState<InteractiveEditingData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const userGlobalData = useSelector(selectUserGlobalData);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedImageForPublish, setSelectedImageForPublish] =
    useState<LibraryItem | null>(null);

  const [isMobile, setIsMobile] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showNewImage, setShowNewImage] = useState(false);

  const scrollTargetRef = useRef<HTMLDivElement>(null);

  const [hasSufficientFlux, setHasSufficientFlux] = useState(true);
  const [isFluxModalOpen, setIsFluxModalOpen] = useState(false);

  useEffect(() => {
    if (userGlobalData) {
      hasEnoughFlux(userGlobalData.user.uid, 5).then(setHasSufficientFlux);
    }
  }, [userGlobalData]);

  const scrollToBottom = () => {
    if (scrollTargetRef.current) {
      scrollTargetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      eventBus.emit("fullscreen:exit");
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchEditingData = async () => {
    if (!id) return;
    try {
      const data = await dreamWorldProvider.getInteractiveEditingData(id);
      setEditingData(data);
    } catch (err) {
      setError("Failed to fetch editing data.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEditingData();
  }, [id]);

  useEffect(() => {
    if (editingData && !isLoading) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [editingData, isLoading]);

  const saveTurnToLibraryAndGetId = async (
    turn: EditingTurn
  ): Promise<string> => {
    if (!userGlobalData || !editingData) {
      throw new Error("User data or editing data is not available.");
    }

    const newLibraryItem: LibraryItem = {
      id: uuidv4(),
      ownerId: userGlobalData.user.uid,
      type: "Image",
      fullUrl: turn.generatedImageUrl,
      thumbnailUrl: turn.generatedImageUrl,
      originalImageUrl: editingData.originalImageUrls[0],
      prompt: turn.request,
      isFavorite: false,
      views: 0,
      likes: 0,
      tags: [editingData.title.toLowerCase()],
      size: "",
      createdAt: Timestamp.now(),
      isPublished: false,
      title: turn.request,
      effectId: editingData.effectId,
    };

    const libraryItemId =
      await dreamWorldProvider.saveLibraryItem(newLibraryItem);
    return libraryItemId;
  };

  const handleSendPrompt = async () => {
    if (!prompt.trim() || !editingData || !userGlobalData) return;

    if (!hasSufficientFlux) {
      setIsFluxModalOpen(true);
      return;
    }

    setIsGenerating(true);

    if (isFullScreen) {
      setIsTransitioning(true);
      setShowNewImage(false);
    }

    const success = await decreaseFluxBalance(userGlobalData.user.uid, 5);
    if (!success) {
      setIsGenerating(false);
      setHasSufficientFlux(false);
      setIsFluxModalOpen(true);
      return;
    }

    try {
      const lastTurn = editingData.editings[editingData.editings.length - 1];
      const lastImageUrl = lastTurn.generatedImageUrl;

      const match = decodeURIComponent(lastImageUrl).match(/o\/(.*?)\?/);
      if (!match) throw new Error("Invalid Firebase URL");
      const objectPath = match[1];
      const imageRef = ref(storage, objectPath);
      const blob = await getBlob(imageRef);
      const file = new File([blob], "last_image.png", { type: blob.type });

      const ai = dreamWorldProvider.getImageAI();
      const fileAsBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = (error) => reject(error);
      });

      const contents = [
        { text: prompt },
        {
          inlineData: {
            mimeType: file.type,
            data: fileAsBase64,
          },
        },
      ];

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-image-preview",
        contents: contents,
        config: {
          responseModalities: [Modality.TEXT, Modality.IMAGE],
        },
      });

      if (
        response.candidates &&
        response.candidates.length > 0 &&
        response.candidates[0].content &&
        response.candidates[0].content.parts
      ) {
        for (const part of response.candidates[0].content.parts) {
          if (part.inlineData && part.inlineData.data) {
            const imageData = part.inlineData.data;
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const newBlob = new Blob([byteArray], { type: "image/png" });
            const newFile = new File([newBlob], "edited_image.png", {
              type: "image/png",
            });
            const generatedImageUrl = await uploadImageToFirebase(
              newFile,
              "postsImages"
            );

            var newTurn: EditingTurn = {
              id: uuidv4(),
              imageUrl: lastImageUrl,
              request: prompt,
              generatedImageUrl,
              date: Timestamp.now(),
              isSavedToLib: true,
              isPublished: false,
            };
            const libraryId = await saveTurnToLibraryAndGetId(newTurn);
            newTurn.libraryItemId = libraryId;
            await dreamWorldProvider.updateInteractiveEditingData(id!, newTurn);

            const updateEditingData = (turn: EditingTurn) => {
              setEditingData((prevData) => {
                if (!prevData) return null;
                const updatedEditings = [...prevData.editings, turn];
                return {
                  ...prevData,
                  editings: updatedEditings,
                  turnsCount: prevData.turnsCount + 1,
                };
              });
            };
            updateEditingData(newTurn);
            setPrompt("");

            if (isFullScreen) {
              setTimeout(() => {
                setShowNewImage(true);
                setIsTransitioning(false);
              }, 800);
            } else {
              setTimeout(() => {
                scrollToBottom();
              }, 100);
            }
          }
        }
      }
    } catch (err) {
      setError("Failed to generate new image.");
      console.error(err);
      if (isFullScreen) {
        setIsTransitioning(false);
        setShowNewImage(false);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublish = async (turn: EditingTurn) => {
    if (
      !editingData ||
      !userGlobalData ||
      turn.isPublished ||
      !turn.libraryItemId
    ) {
      return;
    }
    const libraryItem = await dreamWorldProvider.getLibraryItem(
      turn.libraryItemId
    );

    if (libraryItem) {
      setSelectedImageForPublish(libraryItem);
      setPublishModalOpen(true);
    } else {
      // Handle the case where the library item is not found
      setError("Failed to find the library item to publish.");
    }
  };

  const handleDeleteTurn = async (turn: EditingTurn) => {
    if (!editingData) return;

    try {
      await dreamWorldProvider.deleteEditingTurn(editingData.id, turn);
      setEditingData((prevData) => ({
        ...prevData!,
        editings: prevData!.editings.filter((t) => t.id !== turn.id),
        turnsCount: prevData!.turnsCount - 1,
      }));
    } catch (err) {
      setError("Failed to delete turn.");
      console.error(err);
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    setIsTransitioning(false);
    setShowNewImage(false);
  };

  const getCurrentImage = () => {
    if (!editingData || editingData.editings.length === 0) return null;
    return editingData.editings[editingData.editings.length - 1];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white">
        <LoadingIndicator />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white">
        <div className="bg-red-500/10 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 shadow-2xl">
          <p className="text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  const currentImage = getCurrentImage();

  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white flex flex-col">
      {/* Enhanced Full Screen Mode */}
      <AnimatePresence>
        {isFullScreen && currentImage && !isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="fixed inset-0 z-50"
          >
            {/* Animated Background with Floating Particles */}
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute inset-0"
                style={{
                  background: `
                    radial-gradient(circle at 30% 40%, rgba(168, 85, 247, 0.15) 0%, transparent 50%),
                    radial-gradient(circle at 70% 60%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
                    linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.9) 100%)
                  `,
                  backdropFilter: "blur(40px)",
                }}
              />

              {/* Floating Glass Orbs */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full opacity-10"
                  style={{
                    width: `${Math.random() * 200 + 100}px`,
                    height: `${Math.random() * 200 + 100}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                  animate={{
                    x: [0, Math.random() * 100 - 50, 0],
                    y: [0, Math.random() * 100 - 50, 0],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: Math.random() * 20 + 15,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>

            {/* Control Bar */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="absolute top-0 left-0 right-0 z-[80] p-6"
            >
              <div className="flex items-center justify-between max-w-7xl mx-auto">
                {/* Left Controls */}
                <div className="flex items-center gap-4">
                  {editingData && editingData.editings.length > 1 && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 }}
                      onClick={() => handleDeleteTurn(currentImage)}
                      className="group relative bg-gradient-to-r from-red-500/20 to-pink-500/20 backdrop-blur-xl border border-red-400/30 rounded-2xl p-4 shadow-2xl hover:from-red-500/30 hover:to-pink-500/30 transition-all duration-500"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Trash2
                        size={24}
                        className="text-red-200 group-hover:text-red-100 transition-colors duration-300"
                      />
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </motion.button>
                  )}

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-6 py-3 shadow-xl"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles size={18} className="text-purple-300" />
                      <span className="text-white/90 font-medium">
                        Turn {editingData?.editings.length || 1}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Right Controls */}
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-6 py-3 shadow-xl"
                  >
                    <span className="text-white/70 text-sm font-medium">
                      {editingData?.title}
                    </span>
                  </motion.div>

                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    onClick={toggleFullScreen}
                    className="group relative bg-gradient-to-r from-white/15 to-white/10 backdrop-blur-xl border border-white/30 rounded-2xl p-4 shadow-2xl hover:from-white/25 hover:to-white/15 transition-all duration-500"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <X
                      size={24}
                      className="text-white group-hover:text-white/90 transition-colors duration-300"
                    />
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Main Image Display */}
            <div className="absolute inset-0 flex items-center justify-center p-8 pt-24 pb-32">
              <AnimatePresence mode="wait">
                {!showNewImage ? (
                  <motion.div
                    key="current-image"
                    initial={{ opacity: 1, scale: 1 }}
                    exit={{
                      opacity: 0,
                      scale: 0.9,
                      rotateY: -15,
                      filter: "blur(10px)",
                    }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                    className="relative flex items-center justify-center max-w-6xl max-h-full"
                  >
                    <div className="relative group">
                      {/* Glass Container */}
                      <div className="relative bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-2xl border border-white/20 rounded-3xl p-8 shadow-2xl">
                        <motion.img
                          src={currentImage.generatedImageUrl}
                          alt="Current edit"
                          className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-2xl"
                          style={{
                            filter: isTransitioning
                              ? "brightness(1.3) saturate(1.2) drop-shadow(0 0 40px rgba(168, 85, 247, 0.8))"
                              : "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.6))",
                          }}
                          animate={{
                            filter: isTransitioning
                              ? [
                                  "brightness(1.3) saturate(1.2) drop-shadow(0 0 40px rgba(168, 85, 247, 0.8))",
                                  "brightness(1.5) saturate(1.4) drop-shadow(0 0 60px rgba(59, 130, 246, 0.9))",
                                  "brightness(1.3) saturate(1.2) drop-shadow(0 0 40px rgba(168, 85, 247, 0.8))",
                                ]
                              : "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.6))",
                          }}
                          transition={{
                            duration: isTransitioning ? 2 : 0.5,
                            repeat: isTransitioning ? Infinity : 0,
                          }}
                        />

                        {/* Transition Overlay Effects */}
                        {isTransitioning && (
                          <>
                            <motion.div
                              className="absolute inset-0 rounded-2xl pointer-events-none"
                              style={{
                                background:
                                  "radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, rgba(59, 130, 246, 0.2) 50%, transparent 70%)",
                              }}
                              animate={{
                                opacity: [0.3, 0.7, 0.3],
                                scale: [1, 1.05, 1],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut",
                              }}
                            />

                            {/* Sparkle Effects */}
                            {[...Array(8)].map((_, i) => (
                              <motion.div
                                key={i}
                                className="absolute w-2 h-2 bg-white rounded-full pointer-events-none"
                                style={{
                                  left: `${Math.random() * 100}%`,
                                  top: `${Math.random() * 100}%`,
                                }}
                                animate={{
                                  opacity: [0, 1, 0],
                                  scale: [0, 1, 0],
                                  x: [0, Math.random() * 40 - 20],
                                  y: [0, Math.random() * 40 - 20],
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  delay: Math.random() * 2,
                                }}
                              />
                            ))}
                          </>
                        )}

                        {/* Glass Shine Effect */}
                        <div
                          className="absolute inset-0 rounded-2xl pointer-events-none opacity-20"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 50%, rgba(255, 255, 255, 0.1) 100%)",
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="new-image"
                    initial={{
                      opacity: 0,
                      scale: 1.1,
                      rotateY: 15,
                      filter: "blur(20px)",
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      rotateY: 0,
                      filter: "blur(0px)",
                    }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="relative flex items-center justify-center max-w-6xl max-h-full"
                  >
                    <div className="relative group">
                      {/* Enhanced Glass Container for New Image */}
                      <div className="relative bg-gradient-to-br from-white/15 via-white/8 to-transparent backdrop-blur-2xl border border-white/30 rounded-3xl p-8 shadow-2xl">
                        <motion.img
                          src={
                            editingData?.editings[
                              editingData.editings.length - 1
                            ]?.generatedImageUrl
                          }
                          alt="New edit"
                          className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-2xl"
                          initial={{
                            filter:
                              "brightness(1.8) saturate(1.5) drop-shadow(0 0 60px rgba(168, 85, 247, 1))",
                            scale: 1.02,
                          }}
                          animate={{
                            filter:
                              "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.6))",
                            scale: 1,
                          }}
                          transition={{ duration: 2, ease: "easeOut" }}
                        />

                        {/* Success Glow Effect */}
                        <motion.div
                          className="absolute inset-0 rounded-2xl pointer-events-none"
                          initial={{
                            background:
                              "radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, rgba(168, 85, 247, 0.3) 50%, transparent 70%)",
                            opacity: 1,
                          }}
                          animate={{
                            opacity: 0,
                            scale: 1.2,
                          }}
                          transition={{ duration: 2 }}
                        />

                        {/* Enhanced Glass Shine */}
                        <div
                          className="absolute inset-0 rounded-2xl pointer-events-none opacity-30"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, transparent 30%, rgba(255, 255, 255, 0.2) 70%, transparent 100%)",
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rest of the component remains the same */}
      {isMobile && (
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="fixed top-6 left-4 z-50 bg-black/20 backdrop-blur-xl border border-white/10 rounded-full p-3 shadow-2xl hover:bg-black/30 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.history.back()}
        >
          <ArrowLeft size={20} className="text-white/90" />
        </motion.button>
      )}

      <main
        className={`flex-1 overflow-y-auto p-4 md:p-6 pb-32 ${isFullScreen ? "hidden" : ""}`}
      >
        {editingData && (
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8 mt-16 md:mt-8"
            >
              <div className="relative bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl overflow-hidden">
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-2xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-xl" />

                <div className="relative z-10">
                  <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
                    {editingData.title}
                  </h1>
                  <p className="text-lg text-white/70">
                    The interactive editing journey begins.
                  </p>
                </div>

                {/* Glass shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
              </div>
            </motion.div>

            <div className="space-y-8">
              {editingData.editings.map((turn, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1,
                    ease: "easeOut",
                  }}
                  className="relative group"
                >
                  <div className="relative bg-gradient-to-br from-white/8 via-white/4 to-transparent backdrop-blur-xl border border-white/15 rounded-3xl p-6 md:p-8 shadow-2xl hover:from-white/12 hover:via-white/6 hover:to-transparent transition-all duration-500 overflow-hidden">
                    {/* Background decorative elements */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/5 to-transparent rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-500/5 to-transparent rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative z-10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Original Image */}
                        <div className="relative bg-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10 overflow-hidden group/image">
                          <h3 className="text-lg font-semibold mb-3 text-white/90 flex items-center gap-2">
                            {index === 0 ? (
                              <>
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                                Original Image
                              </>
                            ) : (
                              <>
                                <div className="w-2 h-2 bg-blue-400 rounded-full" />
                                Turn {index}
                              </>
                            )}
                          </h3>
                          {Array.isArray(turn.imageUrl) ? (
                            <div className="grid grid-cols-2 gap-2">
                              {turn.imageUrl.map((url, imgIndex) => (
                                <div
                                  key={imgIndex}
                                  className="relative overflow-hidden rounded-xl"
                                >
                                  <img
                                    src={url}
                                    alt={`Original ${imgIndex + 1}`}
                                    className="w-full shadow-lg border border-white/10 rounded-xl transition-transform duration-500 group-hover/image:scale-105"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-white/10 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="relative overflow-hidden rounded-xl">
                              <img
                                src={
                                  turn.imageUrl ||
                                  "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                                }
                                alt="Original"
                                className="w-full shadow-lg border border-white/10 rounded-xl transition-transform duration-500 group-hover/image:scale-105"
                              />
                              {/* Glass overlay */}
                              <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-white/10 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
                            </div>
                          )}
                        </div>

                        {/* Generated Image */}
                        <div className="relative bg-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10 overflow-hidden group/image">
                          <h3 className="text-lg font-semibold mb-3 text-white/90 flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                            Turn {index + 1}
                          </h3>
                          <div className="relative overflow-hidden rounded-xl">
                            <img
                              src={
                                turn.generatedImageUrl ||
                                "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                              }
                              alt="Generated"
                              className="w-full shadow-lg border border-white/10 rounded-xl transition-transform duration-500 group-hover/image:scale-105"
                            />
                            {/* Glass overlay with gradient */}
                            <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/5 via-transparent to-blue-500/10 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />

                            {/* Sparkle effect on hover */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300">
                              <Sparkles
                                size={16}
                                className="text-white/60 animate-pulse"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Request Display */}
                      <div className="relative mt-6 bg-gradient-to-r from-purple-500/10 via-blue-500/5 to-purple-500/10 backdrop-blur-sm rounded-2xl p-4 border border-purple-500/20 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <p className="relative text-white/90 text-sm leading-relaxed">
                          <span className="inline-flex items-center gap-2 font-semibold text-purple-300 mb-2">
                            <Zap size={16} className="animate-pulse" />
                            Request:
                          </span>
                          <br />
                          <span className="text-white/80">{turn.request}</span>
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-end gap-3 mt-6">
                        <motion.button
                          onClick={() => handlePublish(turn)}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={turn.isPublished}
                          className="group/btn relative flex items-center gap-2 px-4 py-3 rounded-xl backdrop-blur-xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 hover:from-purple-600/30 hover:to-blue-600/30 text-purple-200 border border-purple-500/30 transition-all duration-300 text-sm font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-blue-500/10 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
                          <Share size={16} className="relative z-10" />
                          <span className="relative z-10">
                            {turn.isPublished ? "Published" : "Publish"}
                          </span>
                          {turn.isPublished && (
                            <div className="absolute inset-0 bg-purple-500/20 rounded-xl" />
                          )}
                        </motion.button>

                        {index > 0 && (
                          <motion.button
                            onClick={() => handleDeleteTurn(turn)}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className="group/btn relative flex items-center gap-2 px-4 py-3 rounded-xl backdrop-blur-xl bg-gradient-to-r from-red-500/10 to-pink-500/10 hover:from-red-500/20 hover:to-pink-500/20 text-red-300 border border-red-500/20 transition-all duration-300 text-sm font-medium shadow-lg overflow-hidden"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-pink-500/10 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
                            <Trash2 size={16} className="relative z-10" />
                            <span className="relative z-10">Delete</span>
                          </motion.button>
                        )}
                      </div>
                    </div>

                    {/* Glass shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </motion.div>
              ))}
            </div>
            <div ref={scrollTargetRef} className="h-1" />
            <div style={{ height: "200px", width: "100%" }}></div>
          </div>
        )}
      </main>

      {/* Enhanced Bottom Input for Regular Mode */}
      <div
        className={`fixed bottom-6 left-4 right-4 z-40 ${isFullScreen && !isMobile ? "z-[70]" : ""} ${isFullScreen && isGenerating ? "opacity-0 pointer-events-none" : ""}`}
      >
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative bg-gradient-to-r from-black/25 via-black/15 to-black/25 backdrop-blur-2xl border border-white/20 rounded-3xl p-4 shadow-2xl overflow-hidden"
          >
            {/* Background decorative elements */}
            <div className="absolute top-0 right-1/4 w-16 h-16 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-xl" />
            <div className="absolute bottom-0 left-1/3 w-12 h-12 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-lg" />

            <div className="relative flex items-center gap-3">
              {!isMobile && (
                <motion.button
                  onClick={toggleFullScreen}
                  className="group relative bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-3 shadow-xl hover:from-white/20 hover:to-white/10 transition-all duration-300 overflow-hidden"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  title={
                    isFullScreen ? "Exit full screen" : "Enter full screen"
                  }
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {isFullScreen ? (
                    <Minimize size={20} className="text-white relative z-10" />
                  ) : (
                    <Maximize size={20} className="text-white relative z-10" />
                  )}
                </motion.button>
              )}

              <form
                className="relative flex-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendPrompt();
                }}
              >
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe your next edit..."
                  className="w-full pl-6 pr-16 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400/50 transition-all duration-300 text-white placeholder-white/50 shadow-inner"
                />

                <motion.button
                  type="submit"
                  disabled={
                    !prompt.trim() || isGenerating || !hasSufficientFlux
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20 group overflow-hidden"
                  whileHover={{
                    scale: prompt.trim() && hasSufficientFlux ? 1.05 : 1,
                  }}
                  whileTap={{
                    scale: prompt.trim() && hasSufficientFlux ? 0.95 : 1,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {isGenerating ? (
                    <div className="flex items-center gap-1 relative z-10">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <Zap size={16} className="animate-pulse" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Send size={20} className="relative z-10" />
                      <div className="flex items-center gap-1 text-xs">
                        <Zap size={12} />
                        <span>5</span>
                      </div>
                    </div>
                  )}
                </motion.button>
              </form>
            </div>

            {/* Glass shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
          </motion.div>
        </div>
      </div>

      {/* Publish Modal */}
      {publishModalOpen && selectedImageForPublish && (
        <PublishContent
          stopFullScreenClose={true}
          item={selectedImageForPublish}
          onClose={() => {
            setPublishModalOpen(false);
            setSelectedImageForPublish(null);
          }}
        />
      )}
      <InsufficientFluxModal
        isOpen={isFluxModalOpen}
        onClose={() => setIsFluxModalOpen(false)}
      />
    </div>
  );
};

export default InteractiveEditingPage;
